import React, { Component } from 'react';

import { AccessibilityAPI } from '@philips-emr/tws-components-api';
import { AwesomeComponent, ComponentType } from '@philips-emr/tws-core';
import { bool, eventListener, func, number, string } from '@philips-emr/tws-core/lib/component/prop-types';
import { AccessibilityService, ShortcutService } from '@philips-emr/tws-core/lib/services';
import { msg } from '@philips-emr/tws-core/lib/services/message-service';
import { isFunction, isMobile, trigger } from '@philips-emr/tws-utils';

import { Link } from '..';

import './accessibility.scss';

const { setFontSize, scrollToBottom, scrollToTop, getFontSize, setContrastEnabled, isContrastEnabled, increaseFontSize, decreaseFontSize } = AccessibilityService;

function AccessibilityButton(props) {
  const { active, className, onClick, testId, value, xContrast } = props;
  const activeClass = active ? 'is-active' : '';

  return (
    <button
      className={`c-accessibility__button ${className} ${activeClass}`}
      data-testid={testId}
      onClick={onClick}
      type="button"
      x-contrast={xContrast}>
      {value}
    </button>
  );
}

AccessibilityButton.propTypes = {
  active: bool,
  className: string,
  onClick: func,
  testId: string,
  value: string,
  xContrast: string,
};

@AwesomeComponent({ type: ComponentType.LAYOUT, api: AccessibilityAPI })
class Accessibility extends Component {

  static propTypes = {
    minFontSize: number,
    maxFontSize: number,
    fontSizeStep: number,
    onClick: eventListener,
    onDoubleClick: eventListener,
    onContextMenuClick: eventListener,
    onScrollToMainMenu: eventListener,
  };

  static defaultProps = {
    minFontSize: 70,
    maxFontSize: 150,
    fontSizeStep: 5,
  };

  componentDidMount() {
    if (!isMobile()) {
      const keys = 'alt+1, alt+2, alt+4, alt+5, alt+6, alt+7, alt+8, alt+9, alt+0';
      this.unbindShortcuts = ShortcutService.on(keys, this.handleShortcuts);
    }
  }

  componentWillUnmount() {
    isFunction(this.unbindShortcuts) && this.unbindShortcuts();
  }

  handleShortcuts = (evt, shortcut) => {
    const { onClick, onScrollToMainMenu } = this.props;

    const shortcutActions = {
      'alt+1': scrollToTop,
      'alt+2': trigger(onScrollToMainMenu),
      'alt+4': scrollToBottom,
      'alt+5': trigger(onClick),
      'alt+6': this.resetContrast,
      'alt+7': this.enableContrast,
      'alt+8': this.decreaseFontSize,
      'alt+9': this.resetFontSize,
      'alt+0': this.increaseFontSize,
    };

    shortcutActions[shortcut.key]();
  };

  enableContrast = () => {
    if (!isContrastEnabled()) {
      setContrastEnabled(true);
    }
  };

  resetContrast = () => {
    if (isContrastEnabled()) {
      setContrastEnabled(false);
    }
  };

  toggleContrast = () => {
    setContrastEnabled(!isContrastEnabled());
  };

  updateFontSize() {
    const fontSize = getFontSize();
    this.setState({ fontSize });
  }

  increaseFontSize = () => {
    const { fontSizeStep, maxFontSize } = this.props;

    increaseFontSize(fontSizeStep, maxFontSize);

    this.updateFontSize();
  };

  decreaseFontSize = () => {
    const { fontSizeStep, minFontSize } = this.props;

    decreaseFontSize(fontSizeStep, minFontSize);

    this.updateFontSize();
  };

  resetFontSize = () => {
    setFontSize(100);

    this.updateFontSize();
  };

  render() {
    const { onClick, onDoubleClick, onContextMenuClick } = this.props;
    const fontSize = getFontSize();

    return (
      <div className="c-accessibility" x-contrast="bg">
        <Link
          className="c-accessibility__label"
          onClick={onClick}
          onContextMenu={onContextMenuClick}
          onDoubleClick={onDoubleClick}
          value={msg('tws.accessibility.label')}
        />
        <AccessibilityButton
          className="--toggle-contrast"
          onClick={this.toggleContrast}
          testId="accessibility-high-contrast"
          xContrast="bd bg cl"
        />
        <AccessibilityButton
          active={fontSize < 100}
          className="--font-dec"
          onClick={this.decreaseFontSize}
          testId="accessibility-font-dec"
          value="A-"
          xContrast="bg"
        />
        <AccessibilityButton
          active={fontSize === 100}
          className="--font-reset"
          onClick={this.resetFontSize}
          testId="accessibility-font-reset"
          value="A"
          xContrast="bg"
        />
        <AccessibilityButton
          active={fontSize > 100}
          className="--font-inc"
          onClick={this.increaseFontSize}
          testId="accessibility-font-inc"
          value="A+"
          xContrast="bg"
        />
      </div>
    );
  }
}

export default Accessibility;
